'use client';

import { getData, isNetworkError } from '@/utils/api-helpers';
import { useUserStore } from '@/store/useUserStore';
import { AlertTriangle, Eye, Loader } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

export const WATCH_URL = 'https://virtual.image-nation.org/landing';

interface WatchButtonProps {
    ticketAllocationId: string;
    cinesendVideoId: string;
    voucherCode: {
        url: string;
        code: string;
        expiresAt: string | null;
        firstAccessedAt: string | null;
    } | null;
}

const WatchButton = (props: WatchButtonProps) => {
    const { ticketAllocationId, cinesendVideoId, voucherCode } = props;
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [expirationDate, setExpirationDate] = useState<Date | null>(null);
    const t = useTranslations('Common');
    const locale = useLocale();
    const { session } = useUserStore();

    // Update expiration date when voucherCode changes
    useEffect(() => {
        if (voucherCode?.expiresAt) {
            // Parse ISO 8601 date string (backend should return with timezone, e.g., "2025-01-20T12:00:00Z")
            // JavaScript Date constructor automatically handles timezone conversion
            const parsedDate = new Date(voucherCode.expiresAt);

            // Validate that the date was parsed correctly
            if (!isNaN(parsedDate.getTime())) {
                setExpirationDate(parsedDate);
            } else {
                console.warn('Invalid expiration date format:', voucherCode.expiresAt);
                setExpirationDate(null);
            }
        } else {
            setExpirationDate(null);
        }
    }, [voucherCode?.expiresAt]);

    // Calculate hours until expiration (for display only)
    // Uses getTime() which returns UTC milliseconds, making the comparison timezone-agnostic
    const getTimeUntilExpiration = (): number | null => {
        if (!expirationDate) return null;

        const now = new Date();
        // getTime() returns milliseconds since epoch in UTC, so comparison is timezone-agnostic
        const diff = expirationDate.getTime() - now.getTime();

        if (diff <= 0) return null; // Expired (estimate)

        // Return hours remaining (rounded down)
        return Math.floor(diff / (1000 * 60 * 60));
    };

    const hoursUntilExpiry = getTimeUntilExpiration();

    /**
     * Retry logic with exponential backoff
     * Only retries on network errors and timeouts, not HTTP 4xx/5xx errors
     */
    const fetchWithRetry = async (endpoint: string, maxAttempts: number = 3): Promise<any> => {
        let lastError: Error | null = null;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                // Show retry message if not first attempt
                if (attempt > 1) {
                    setErrorMessage(
                        t('networkErrorRetry', {
                            attempt,
                            maxAttempts,
                        }) || `Retrying... (${attempt}/${maxAttempts})`,
                    );
                }

                const response = await getData(endpoint, locale, session?.access_token);
                return response;
            } catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                console.error(`Attempt ${attempt}/${maxAttempts} failed:`, lastError);

                // Check if this is a retryable error (network error or timeout)
                const isRetryable =
                    isNetworkError(error) ||
                    (error instanceof Error &&
                        (error.name === 'AbortError' || error.message.includes('timed out')));

                // Don't retry on HTTP errors (4xx, 5xx) or on last attempt
                if (!isRetryable || attempt >= maxAttempts) {
                    throw lastError;
                }

                // Exponential backoff: 1s, 2s, 4s
                const delay = Math.pow(2, attempt - 1) * 1000;
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }

        // All attempts failed
        throw lastError || new Error('Failed to fetch voucher after multiple attempts');
    };

    const handleClick = async () => {
        setErrorMessage(null);

        if (voucherCode && voucherCode.code && voucherCode.url) {
            // Voucher exists - just open it
            // Backend guarantees it's valid if it exists
            window.open(voucherCode.url, '_blank');

            // Update expiration if available
            if (voucherCode.expiresAt) {
                const parsedDate = new Date(voucherCode.expiresAt);
                if (!isNaN(parsedDate.getTime())) {
                    setExpirationDate(parsedDate);
                }
            }
        } else {
            setLoading(true);
            setErrorMessage(null);

            try {
                let endpoint: string;
                if (cinesendVideoId) {
                    // For passes watching specific films
                    endpoint = `/cinesend/player/${cinesendVideoId}?ticketAllocationId=${ticketAllocationId}`;
                } else {
                    // For tickets with specific screenings
                    endpoint = `/cinesend/voucher/${ticketAllocationId}`;
                }

                const response = await fetchWithRetry(endpoint, 3);

                if (response && response.url) {
                    // Backend has created voucher and tracked access
                    window.open(response.url, '_blank');

                    // Store expiration for UI display
                    // Backend should return ISO 8601 string with timezone (e.g., "2025-01-20T12:00:00Z")
                    if (response.expiresAt) {
                        const parsedDate = new Date(response.expiresAt);
                        if (!isNaN(parsedDate.getTime())) {
                            setExpirationDate(parsedDate);
                        } else {
                            console.warn(
                                'Invalid expiration date from backend:',
                                response.expiresAt,
                            );
                        }
                    }
                } else {
                    setErrorMessage(t('voucherGenerationFailed'));
                }
            } catch (error) {
                console.error('[WatchButton] Error generating voucher after retries:', {
                    error,
                    ticketAllocationId,
                    cinesendVideoId,
                    endpoint: cinesendVideoId
                        ? `/cinesend/player/${cinesendVideoId}?ticketAllocationId=${ticketAllocationId}`
                        : `/cinesend/voucher/${ticketAllocationId}`,
                });

                // Use user-friendly error message
                if (error instanceof Error) {
                    setErrorMessage(error.message || t('voucherGenerationFailed'));
                } else {
                    setErrorMessage(t('voucherGenerationFailed'));
                }
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="flex flex-col items-end gap-2">
            {/* Show expiration warning if less than 24 hours remaining (estimate) */}
            {hoursUntilExpiry !== null && hoursUntilExpiry < 24 && hoursUntilExpiry > 0 && (
                <div className="flex items-center gap-2 text-yellow-500 text-xs uppercase">
                    <AlertTriangle className="w-4 h-4" />
                    {t('expiresIn', { hours: hoursUntilExpiry })}
                </div>
            )}
            {errorMessage && (
                <div className="flex items-center gap-2 text-red-500 text-xs uppercase max-w-[200px] text-right">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>{errorMessage}</span>
                </div>
            )}
            <button
                className="max-w-[100px] min-w-[100px] w-full text-primary text-md lg:text-h6 uppercase flex justify-end items-center gap-2 text-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleClick}
                disabled={loading}
            >
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        {t('watch')}
                        <Eye className="min-w-6 min-h-6 max-xl:mr-4" />
                    </>
                )}
            </button>
        </div>
    );
};

export default WatchButton;
